import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import type { NextPage } from 'next';

import { getTestId } from '@dansk-metal/service/testing';
import { Container, InputText, Select } from '@dansk-metal/ui';

import { ContentDrawer } from '@web/components/content-drawer/content-drawer';
import MaybeLink from '@web/components/department-link/department-link';
import { EmptyState } from '@web/components/empty-state/empty-state';
import { ErrorFrame } from '@web/components/error-frame/error-frame';
import HeroTitle from '@web/components/hero-block/hero-title/hero-title';
import { Pagination } from '@web/components/pagination/pagination';
import { ThemedSkeleton } from '@web/components/themed-skeleton/themed-skeleton';

import TemplateLayout from '@web/layouts/template/template';

import { useAuthenticatedCRMRequestConfig } from '@web/services/crm-api/hooks';
import { CompanyMemberDto, useGetCompanyById, useGetDepartmentById, useGetMemberById } from '@web/services/crm-api/rest';
import { crmError } from '@web/services/errorhandler/appError';
import { MappedDepartment } from '@web/services/umbraco/queries/departments';
import { HeroAlignment } from '@web/services/umbraco/types/basic/Alignment';
import { BackgroundColor } from '@web/services/umbraco/types/basic/Color';
import { UmbracoErrorType } from '@web/services/umbraco/types/basic/ErrorTypes';
import { UmbracoPage } from '@web/services/umbraco/types/documents/UmbracoDocument';

import { MemberDrawerContent } from '@web/templates/union-rep-overview-page/components/member-drawer-content';
import { MembersListItem } from '@web/templates/union-rep-overview-page/components/members-list-item';
import { useMembersSearchForm } from '@web/templates/union-rep-overview-page/hooks/use-members-search-form';

import { getPhoneNumber } from '@web/utils/format-number';

import styles from './union-rep-overview-page.module.scss';

export const unionRepOverviewPageContentAlias = 'unionRepOverviewPage' as const;

export type UnionRepOverviewPageData = UmbracoPage<
	typeof unionRepOverviewPageContentAlias,
	{
		hero: {
			title: string;
			subtitle?: string;
		};
	},
	{
		departments: MappedDepartment[];
	}
>;

const defaultListSkeletonCount = 20;
const detailsSkeletonCount = 3;
const defaultAgreementsSkeletonCount = 2;

const UnionRepOverviewPageTemplate: NextPage<{ pageData: UnionRepOverviewPageData } > = ({ pageData }) => {
	const { hero } = pageData.properties;
	const [skeletonCount, setSkeletonCount] = useState(20);
	const { memberId: loggedMemberId, requestConfig, isMember } = useAuthenticatedCRMRequestConfig();

	const {
		data: loggedUserData,
		isLoading: loggedUserLoading,
		error: loggedUserError,
	} = useGetMemberById(loggedMemberId, { request: requestConfig });

	const departmentId = loggedUserData?.departmentId ? loggedUserData.departmentId : '';
	const memberCompanyId = loggedUserData?.employer?.companyId ? loggedUserData.employer.companyId : '';
	const departmentRoute = pageData.departments.find((each) => each.departmentId === departmentId)?.url;

	const {
		data: company,
		isLoading: companyLoading,
		error: companyError,
	} = useGetCompanyById(memberCompanyId, { query: { enabled: !!memberCompanyId }, request: requestConfig });

	const {
		data: department,
		isLoading: departmentLoading,
		error: departmentError,
	} = useGetDepartmentById(departmentId);

	const {
		control,
		query,
		pagination,
		submit,
		membershipTypeSelect,
		handleSubmit,
		handleSearchByName,
		total,
	} = useMembersSearchForm({
		memberId: loggedMemberId,
		companyId: loggedUserData?.employer?.companyId || '',
		limit: 20,
	});

	const pageLoading = loggedUserLoading || departmentLoading || companyLoading || !loggedMemberId;

	useEffect(() => {
		handleSubmit(submit)();
	}, [handleSubmit, submit]);

	// Set the count for the loading skeletons only after data is loaded so that the number of skeleton items is the same as the previous query
	useEffect(() => {
		if (!pageLoading && !query.isLoading) {
			setSkeletonCount(total);
		}
	}, [pageLoading, query.isLoading, total]);

	const [selectedMember, setSelectedMember] = useState<CompanyMemberDto | null>(null);
	const [isOpen, setIsOpen] = useState<boolean>(false);

	function handleOpenMemberDrawer(member: CompanyMemberDto) {
		setSelectedMember(member);
		setIsOpen(true);
	}

	function handleCloseMemberDrawer() {
		setSelectedMember(null);
		setIsOpen(false);
	}

	if (!isMember) {
		return <ErrorFrame errorType={UmbracoErrorType.Forbidden} />;
	}

	if (query.error || loggedUserError || departmentError || companyError) {
		throw crmError({ cause: query.error || loggedUserError || departmentError || companyError as unknown as Error });
	}

	if (loggedUserData && !(loggedUserData?.flags?.isTillidsRepresentant || loggedUserData?.flags?.isArbejdsmarkedRepresentant)) {
		return <ErrorFrame errorType={UmbracoErrorType.Forbidden} />;
	}

	return (
		<TemplateLayout>
			{!!hero.title && (
				<HeroTitle
					title={hero.title}
					subtitle={hero.subtitle}
					alignment={HeroAlignment.Left}
					background={BackgroundColor.White}
				/>
			)}
			<Container>
				<div className={styles.details}>
					<div className={styles.details__address}>
						<h2 className="h5" >Virksomhed</h2>
						{!pageLoading ? (
							<p>
								<span>{company?.name}</span>
								<span>{company?.address.addressLine}</span>
								<span>{`${company?.address?.zipCode || ''} ${company?.address?.city || ''}`}</span>
							</p>
						) : (
							<ThemedSkeleton count={detailsSkeletonCount} height={24} />
						)}
					</div>
					<div className={styles.details__member}>
						<h2 className="h5">Lokalafdeling</h2>
						{!pageLoading ? (
							<p>
								{department?.name && (
									<MaybeLink route={departmentRoute} name={department.name} />
								)}
								{department?.phoneNumber && (
									<span>{getPhoneNumber(department.phoneNumber)}</span>
								)}
								<span>{department?.email}</span>
							</p>
						) : (
							<ThemedSkeleton	count={detailsSkeletonCount} height={24}/>
						)}
					</div>
					<div className={styles.details__agreements}>
						<h2 className="h5">Overenskomster</h2>
						{!pageLoading ? (
							<p>
								{company?.agreements && company?.agreements && company.agreements.map((agreement) => (
									<span key={agreement.companyAgreementId}>{agreement.companyAgreementName}</span>
								))}
								{!company?.agreements && (
									<span>-</span>
								)}
							</p>
						) : (
							<ThemedSkeleton count={defaultAgreementsSkeletonCount} height={24}/>
						)}
					</div>
				</div>
			</Container>
			<div>
				<Container>
					<form>
						<div className={styles.members__filters}>
							<Select
								{...membershipTypeSelect}
								className={styles.filter_inputs}
								noOptionalLabel
								label="Medlemstype"
							/>
							<Controller
								control={control}
								name="name"
								render={({ field }) => (
									<InputText
										{...field}
										onChange={(inputText) => handleSearchByName(inputText)}
										className={styles.filter_inputs}
										noOptionalLabel
										label="Navn"
										icon={{ icon: 'search' }}
									/>
								)}
							/>
						</div>
					</form>
				</Container>
				{(query.isLoading || pageLoading) && (
					<ThemedSkeleton
						containerClassName={styles.skeleton_theme_container}
						className={styles.skeleton_theme}
						count={skeletonCount || defaultListSkeletonCount}
						inline={true}
					/>
				)}
				{(!query.isLoading && !pageLoading) && (
					<div>
						{query.data?.meta.total && query.data?.meta.total > 0 ? (
							<div className={styles.members__list}>
								{query.data?.items.map((member) => (
									<MembersListItem
										key={member.memberId}
										member={member}
										onClickHandler={() => { handleOpenMemberDrawer(member || null); }}
									/>
								))}
								{pagination.maxPages > 1 && (
									<div>
										<Pagination {...pagination} />
									</div>
								)}
							</div>
						) : (
							<EmptyState
								title='Ingen medlemmer'
								bodyText='Der er ingen medlemmer, som matcher de filtre du har sat.'
							/>
						)}
					</div>
				)}
			</div>
			<ContentDrawer
				open={isOpen}
				onClose={handleCloseMemberDrawer}
				testId={getTestId('content_drawer')}
			>
				{selectedMember && (
					<MemberDrawerContent member={selectedMember} />
				)}
			</ContentDrawer>
		</TemplateLayout>
	);
};

export default UnionRepOverviewPageTemplate;
